import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, Clock, X as XIcon, RefreshCw } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../../lib/adminApi";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const fmt = (n) => Number.isFinite(+n) ? (+n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—";
const badge = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};
const iconFor = { paid: Check, pending: Clock, rejected: XIcon };

export default function WithdrawalsManager() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [ws, cs] = await Promise.all([
        adminApi.get("/admin/withdrawals"),
        adminApi.get("/admin/customers"),
      ]);
      setItems(ws.data);
      setUsers(Object.fromEntries(cs.data.map((c) => [c.id, c])));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => items.reduce((a, t) => { a[t.status] = (a[t.status] || 0) + 1; return a; }, {}), [items]);
  const filtered = filter === "all" ? items : items.filter((t) => t.status === filter);

  const setStatus = async (id, status) => {
    try {
      await adminApi.put(`/admin/withdrawals/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="withdrawals-manager-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="font-display text-lg font-semibold text-[#0B192C]">
          Withdrawals ({items.length}) <span className="text-sm font-normal text-slate-500">— {counts.pending || 0} pending · {counts.paid || 0} paid</span>
        </h3>
        <div className="flex gap-2">
          {["all", "pending", "paid", "rejected"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`wd-filter-${f}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors ${filter === f ? "bg-[#0B192C] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#0B192C]"}`}>
              {f}{f !== "all" ? ` (${counts[f] || 0})` : ""}
            </button>
          ))}
          <Button variant="outline" onClick={load} className="rounded-full h-9 px-4 text-sm" data-testid="wd-refresh">
            <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-slate-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-slate-500 text-sm" data-testid="wd-empty">No withdrawals in this view.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="withdrawals-table">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">Payout to</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((w) => {
                  const u = users[w.user_id];
                  const Icon = iconFor[w.status] || Clock;
                  return (
                    <tr key={w.id} data-testid={`admin-wd-row-${w.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-display font-semibold text-[#0B192C]">{u ? `${u.first_name} ${u.last_name}` : "Unknown"}</div>
                        <div className="text-xs text-slate-500">{u?.email || w.user_id.slice(0, 8)}</div>
                      </td>
                      <td className="px-4 py-3 font-display font-semibold text-[#0B192C]">{w.currency === "CAD" ? "$" : "₦"}{fmt(w.amount)} <span className="text-slate-500 font-normal">{w.currency}</span></td>
                      <td className="px-4 py-3 text-slate-700 capitalize">{w.method.replace(/_/g, " ")}</td>
                      <td className="px-4 py-3">
                        <div className="text-[#0B192C] font-medium">{w.account_name}</div>
                        <div className="text-xs text-slate-500 break-all">{w.account_details}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-semibold ${badge[w.status] || badge.pending}`}>
                          <Icon size={12} /> {w.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{new Date(w.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">
                        <Select value={w.status} onValueChange={(v) => setStatus(w.id, v)}>
                          <SelectTrigger className="h-9 w-32 rounded-lg" data-testid={`wd-status-select-${w.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
